import { apiClient } from '../../services/api';
import { createClientRequestId } from '../../utils/clientRequestId';

// WP8 target-v2 settlement (dark). The Business side of handing a trip's cash
// over, investigating a count that did not balance, and owning cash a customer
// was never physically given back.
//
// None of these reconcile anything. `REMIT_CONFIRMED` closes physical cash
// custody and nothing else; a resolved discrepancy closes an investigation; a
// closed obligation is a declaration with a reason on it. The screens say so and
// so does every response.

const BASE = '/api/business/delivery';
const HEADERS = { 'X-Portal': 'business' };
const unwrap = (response) => response?.data?.data ?? response?.data ?? response;

const key = (prefix, supplied) => supplied || createClientRequestId(prefix);

export const businessSettlementService = Object.freeze({
  // --- the counter -------------------------------------------------------
  async remittances(businessId, status, signal) {
    return unwrap(await apiClient.get(`${BASE}/remittances`, {
      headers: HEADERS, params: { businessId, status }, signal,
    }));
  },

  /**
   * Count what the Rider handed over.
   *
   * `countedAmountCents` is what the receiver physically counted. It is not
   * reconciliation and it is not the expected figure: the server derives that
   * under lock from the session's own frozen rows, and a mismatch opens a
   * discrepancy rather than being quietly corrected here.
   */
  async confirmRemittance(businessId, sessionId, payload = {}) {
    return unwrap(await apiClient.post(`${BASE}/remittances/${sessionId}/confirm`, {
      businessId,
      countedAmountCents: payload.countedAmountCents,
      expectedVersion: payload.expectedVersion,
      notes: payload.notes || undefined,
    }, { headers: { ...HEADERS, 'Idempotency-Key': key('remit_confirm', payload.idempotencyKey) } }));
  },

  // --- the investigation -------------------------------------------------
  async discrepancies(businessId, filters = {}, signal) {
    return unwrap(await apiClient.get(`${BASE}/remittance-discrepancies`, {
      headers: HEADERS, params: { businessId, ...filters }, signal,
    }));
  },

  /**
   * Close a cash discrepancy.
   *
   * The person who counted the cash cannot be the person who closes the case:
   * that is a storage-level rule, not a courtesy. `ownerOverride` is the only
   * same-actor path and it must state why nobody else could do it.
   */
  async resolveDiscrepancy(businessId, caseId, payload = {}) {
    return unwrap(await apiClient.post(`${BASE}/remittance-discrepancies/${caseId}/resolve`, {
      businessId,
      resolutionCategory: payload.resolutionCategory,
      correctedCountCents: payload.correctedCountCents ?? undefined,
      resolutionReason: payload.resolutionReason,
      expectedVersion: payload.expectedVersion,
      ownerOverride: payload.ownerOverride || undefined,
    }, { headers: { ...HEADERS, 'Idempotency-Key': key('discrepancy_resolve', payload.idempotencyKey) } }));
  },

  // --- the debt ----------------------------------------------------------
  async cashObligations(businessId, filters = {}, signal) {
    return unwrap(await apiClient.get(`${BASE}/cash-obligations`, {
      headers: HEADERS, params: { businessId, ...filters }, signal,
    }));
  },

  async assignObligationOwner(businessId, obligationId, payload = {}) {
    return unwrap(await apiClient.post(`${BASE}/cash-obligations/${obligationId}/owner`, {
      businessId, ownerBusinessMemberId: payload.ownerBusinessMemberId,
    }, { headers: { ...HEADERS, 'Idempotency-Key': key('obligation_owner', payload.idempotencyKey) } }));
  },

  // The only way an obligation is settled: the amount goes to the shopper's
  // Apparify Wallet once the Rider's cash has reached the store.
  async refundObligationToWallet(businessId, obligationId, payload = {}) {
    return unwrap(await apiClient.post(`${BASE}/cash-obligations/${obligationId}/refund-to-wallet`, {
      businessId, expectedVersion: payload.expectedVersion,
    }, { headers: { ...HEADERS, 'Idempotency-Key': key('obligation_refund', payload.idempotencyKey) } }));
  },

  // --- the consequences --------------------------------------------------
  async riderWorkHolds(businessId, riderBusinessMemberId, signal) {
    return unwrap(await apiClient.get(`${BASE}/rider-work-holds`, {
      headers: HEADERS, params: { businessId, riderBusinessMemberId }, signal,
    }));
  },

  async settlementDestinations(businessId, runId, signal) {
    return unwrap(await apiClient.get(`${BASE}/runs/${runId}/settlement-destinations`, {
      headers: HEADERS, params: { businessId }, signal,
    }));
  },

  /**
   * Send a Rider to a different company location.
   *
   * A redirection, never a receipt: it moves one obligation kind and closes
   * nothing. The two-party handover at the new destination is still the only
   * thing that discharges custody.
   */
  async authorizeContingency(businessId, runId, payload = {}) {
    return unwrap(await apiClient.post(`${BASE}/runs/${runId}/settlement-contingencies`, {
      businessId,
      obligationKind: payload.obligationKind,
      replacementLocationId: payload.replacementLocationId,
      reasonCode: payload.reasonCode,
      reason: payload.reason,
    }, { headers: { ...HEADERS, 'Idempotency-Key': key('settlement_contingency', payload.idempotencyKey) } }));
  },

  async contingencies(businessId, signal) {
    return unwrap(await apiClient.get(`${BASE}/settlement-contingencies`, {
      headers: HEADERS, params: { businessId }, signal,
    }));
  },
});

export default businessSettlementService;
